import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { CustomButton } from "../../../components";

// ==================== PREVIEW ROW COMPONENT ====================
const PreviewRow = ({ label, value }) => {
  // if (value === null || value === undefined || value === "") return null;
  return (
    <div style={{ display: "flex", borderBottom: "1px solid #e0e0e0" }}>
      <div
        style={{
          flex: "0 0 40%",
          padding: "10px 16px",
          fontWeight: 600,
          fontSize: "14px",
          backgroundColor: "#fafafa",
          wordBreak: "break-word",
          whiteSpace: "normal",
          overflowWrap: "break-word",
        }}
      >
        {label}
      </div>
      <div
        style={{
          flex: "0 0 60%",
          padding: "10px 16px",
          fontSize: "14px",
          wordBreak: "break-word",
          whiteSpace: "normal",
          overflowWrap: "break-word",
        }}
      >
        {value || "-"}
      </div>
    </div>
  );
};

// ==================== SECTION HEADER ====================
const SectionHeader = ({ title }) => (
  <div
    style={{
      fontWeight: 700,
      padding: "14px 16px 10px",
      fontSize: "15px",
      color: "#1a237e",
      borderBottom: "2px solid #1a237e",
      backgroundColor: "#e8eaf6",
    }}
  >
    {title}
  </div>
);

// ==================== MAIN MODAL COMPONENT ====================
const ModalX = ({
  open,
  onClose,
  formData,
  onSubmit,
  submitting,
  handleVerifyApplication,
  generatedOtp,
  otpInputRef,
}) => {
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);

  const [agreed, setAgreed] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  // Confirm & Submit is enabled only when checkbox ticked AND OTP verified
  const canSubmit = agreed && otpVerified;

  // ← Auto-focus OTP input whenever the checkbox is ticked
  useEffect(() => {
    // Small delay lets React finish rendering the conditionally shown input
    setTimeout(() => otpInputRef.current?.focus(), 50);
  }, [agreed, otpInputRef]);

  // ---- OTP input handler — numbers only + live match check ----
  const handleOtpChange = (e) => {
    const raw = e.target.value;

    // Allow only digits
    if (raw !== "" && !/^\d*$/.test(raw)) return;
    // Cap at 6 digits
    const val = raw.slice(0, 6);
    setOtp(val);

    if (val.length === 0) {
      setOtpError("");
      setOtpVerified(false);
      return;
    }

    if (val.length === 6) {
      if (val === generatedOtp) {
        setOtpError("");
        setOtpVerified(true);
      } else {
        setOtpError(t?.invalidOtp || "Invalid OTP. Please try again.");
        setOtpVerified(false);
      }
    } else {
      // Still typing — clear previous error
      setOtpError("");
      setOtpVerified(false);
    }
  };

  // ---- Confirm & Submit ----
  const handleConfirmSubmit = () => {
    if (!otpVerified) {
      setOtpError(t?.invalidOtp || "Invalid OTP. Please try again.");
      return;
    }
    onSubmit(otp);
  };

  // ---- Resend OTP ----
  const handleResend = (e) => {
    setOtp("");
    setOtpError("");
    setOtpVerified(false);
    handleVerifyApplication(e);
  };

  // ---- Reset on close ----
  const handleClose = () => {
    setAgreed(false);
    setOtp("");
    setOtpError("");
    setOtpVerified(false);
    onClose();
  };

  // ---- Format timer ----
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getMediumLabel = (val) => (val === "TM" ? "TM" : "EM");

  const getDisplayValue = (val) => {
    if (!val) return "";
    if (typeof val === "object" && val.label) return val.label;
    return val;
  };

  const getRawValue = (val) => {
    if (!val) return "";
    if (typeof val === "object" && val.value !== undefined) return val.value;
    return val;
  };

  const livingWithValue = getRawValue(formData.living_with);
  const isParent = livingWithValue === "parent";
  const studentName = formData.student_name || "the student";

  return (
    <Modal
      show={open}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      scrollable
    >
      {/* ---- Modal Header ---- */}
      <Modal.Header
        closeButton
        style={{
          background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
          color: "#fff",
          borderBottom: "none",
        }}
      >
        <Modal.Title style={{ fontSize: "18px", fontWeight: 600 }}>
          {t?.applicationPreview || "Application Preview"}
        </Modal.Title>
      </Modal.Header>

      {/* ---- Modal Body ---- */}
      <Modal.Body style={{ padding: 0, maxHeight: "65vh", overflowY: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <tbody>
            {/* ======= BASIC DETAILS ======= */}
            <SectionHeader title={t?.basicDetails || "Basic Details"} />
            <PreviewRow label={t?.date || "Date"} value={formData.date} />
            <PreviewRow
              label={t?.studentNameTamil || "Student's Name (Tamil)"}
              value={formData.student_name_in_tamil}
            />
            <PreviewRow
              label={t?.studentNameEnglish || "Student's Name (English)"}
              value={formData.student_name}
            />
            <PreviewRow
              label={t?.dateOfBirth || "Date of Birth"}
              value={
                formData.date_of_birth
                  ? moment(formData.date_of_birth).format("YYYY-MM-DD")
                  : ""
              }
            />
            <PreviewRow
              label={t?.dobInWords || "DOB in Words"}
              value={formData.date_of_birth_words}
            />
            <PreviewRow
              label={t?.aadharNumber || "Aadhar No."}
              value={formData.aadhar_no}
            />
            <PreviewRow
              label={t?.emailAddress || "Email"}
              value={formData.email}
            />
            <PreviewRow
              label={t?.mobileNumber || "Mobile No."}
              value={formData.mobile_number}
            />
            <PreviewRow
              label={t?.gender || "Gender"}
              value={getDisplayValue(formData.gender)}
            />
            <PreviewRow
              label={t?.religion || "Religion"}
              value={getDisplayValue(formData.religion)}
            />
            <PreviewRow label={t?.caste || "Caste"} value={formData.caste} />
            <PreviewRow
              label={t?.category || "Category"}
              value={getDisplayValue(formData.community)}
            />
            <PreviewRow
              label={t?.nationality || "Nationality"}
              value={formData.nationality}
            />
            <PreviewRow
              label={t?.state || "State"}
              value={getDisplayValue(formData.student_native_state)}
            />

            {/* ======= RESIDENTIAL DETAILS ======= */}
            <SectionHeader
              title={t?.residentialDetails || "Residential Details"}
            />
            <PreviewRow
              label={
                t?.areYouResidingWith ||
                "Are you residing with parents or with a guardian?"
              }
              value={getDisplayValue(formData.living_with)}
            />
            <PreviewRow
              label={t?.fullname || "Name"}
              value={isParent ? formData.parent_name : formData.guardian_name}
            />
            <PreviewRow
              label={t?.occupation || "Occupation"}
              value={
                isParent
                  ? getDisplayValue(formData.parent_occupation)
                  : getDisplayValue(formData.guardian_occupation)
              }
            />
            <PreviewRow
              label={t?.annualIncome || "Annual Income"}
              value={
                isParent
                  ? formData.parent_annual_salary || ""
                  : formData.guardian_annual_salary || ""
              }
            />
            <PreviewRow
              label={t?.address || "Address"}
              value={
                isParent ? formData.parent_address : formData.guardian_address
              }
            />
            <PreviewRow
              label={t?.city || "City"}
              value={isParent ? formData.parent_city : formData.guardian_city}
            />
            <PreviewRow
              label={t?.district || "District"}
              value={
                isParent ? formData.parent_district : formData.guardian_district
              }
            />
            <PreviewRow
              label={t?.state || "State"}
              value={
                isParent
                  ? getDisplayValue(formData.parent_state)
                  : getDisplayValue(formData.guardian_state)
              }
            />
            <PreviewRow
              label={t?.pincode || "Pincode"}
              value={
                isParent ? formData.parent_pincode : formData.guardian_pincode
              }
            />

            {/* ======= LAST STUDIED DETAILS ======= */}
            <SectionHeader
              title={t?.lastStudiedDetails || "Last Studied Details"}
            />
            <PreviewRow
              label={t?.lastStudiedClass || "Last Studied Class"}
              value={getDisplayValue(formData.last_studied_class)}
            />
            <PreviewRow
              label={t?.lastStudiedSchool || "Last Studied School"}
              value={formData.last_studied_school}
            />
            <PreviewRow
              label={t?.emisNo || "EMIS No"}
              value={formData.emis_id}
            />
            <PreviewRow
              label={t?.lastStudiedMedium || "Last Studied Medium"}
              value={getMediumLabel(formData.last_studied_medium)}
            />
            <PreviewRow
              label={
                t?.eligibleForHigherStudies || "Eligible for Higher Studies"
              }
              value={formData.qualified_higher_secondary}
            />
            <PreviewRow
              label={
                t?.attachedDocumentType ||
                "Attached Document (Transfer Certificate / Record Sheet)"
              }
              value={
                formData.attached_document_type === "TC" ? "TC" : "Record Sheet"
              }
            />
            <PreviewRow
              label={t?.document || "Document"}
              value={formData.document_original_name}
            />

            {/* ======= PREFERRED COURSE DETAILS ======= */}
            <SectionHeader
              title={t?.preferredCourseDetails || "Preferred Course Details"}
            />
            <PreviewRow
              label={
                t?.preferredClassForAdmission || "Preferred Class for Admission"
              }
              value={getDisplayValue(formData.preferred_class)}
            />
            <PreviewRow
              label={t?.motherTongue || "Mother Tongue"}
              value={getDisplayValue(formData.mother_tongue)}
            />
            <PreviewRow
              label={
                t?.preferredFirstLanguageToStudy ||
                "Preferred First Language to Study"
              }
              value={getDisplayValue(formData.preferred_first_language)}
            />
            <PreviewRow
              label={t?.preferredMediumToStudy || "Preferred Medium to Study"}
              value={getMediumLabel(formData.preferred_stream)}
            />
            <PreviewRow
              label={
                t?.smallpoxVaccinationTaken || "Smallpox Vaccination Taken?"
              }
              value={formData.is_vaccinated_pox}
            />

            {/* ======= PREVIOUS SCHOOL ADDRESS ======= */}
            {formData.previous_school_address_1 && (
              <>
                <SectionHeader
                  title={t?.previousSchoolAddress || "Previous School Address"}
                />
                <PreviewRow
                  label={`${t?.schoolName || "School Name"} 1`}
                  value={formData.previous_school_address_1}
                />
                <PreviewRow
                  label={t?.classes || "Classes"}
                  value={getDisplayValue(formData.previous_studied_class_1)}
                />
                <PreviewRow
                  label={t?.yearOfStudy || "Year of Study"}
                  value={getDisplayValue(formData.previous_studied_year_1)}
                />
                <PreviewRow
                  label={t?.mediumOfInstruction || "Medium of Instruction"}
                  value={getMediumLabel(formData.previous_studied_medium_1)}
                />
              </>
            )}
            {formData.previous_school_address_2 && (
              <>
                <PreviewRow
                  label={`${t?.schoolName || "School Name"} 2`}
                  value={formData.previous_school_address_2}
                />
                <PreviewRow
                  label={t?.classes || "Classes"}
                  value={getDisplayValue(formData.previous_studied_class_2)}
                />
                <PreviewRow
                  label={t?.yearOfStudy || "Year of Study"}
                  value={getDisplayValue(formData.previous_studied_year_2)}
                />
                <PreviewRow
                  label={t?.mediumOfInstruction || "Medium of Instruction"}
                  value={getMediumLabel(formData.previous_studied_medium_2)}
                />
              </>
            )}
            {formData.previous_school_address_3 && (
              <>
                <PreviewRow
                  label={`${t?.schoolName || "School Name"} 3`}
                  value={formData.previous_school_address_3}
                />
                <PreviewRow
                  label={t?.classes || "Classes"}
                  value={getDisplayValue(formData.previous_studied_class_3)}
                />
                <PreviewRow
                  label={t?.yearOfStudy || "Year of Study"}
                  value={getDisplayValue(formData.previous_studied_year_3)}
                />
                <PreviewRow
                  label={t?.mediumOfInstruction || "Medium of Instruction"}
                  value={getMediumLabel(formData.previous_studied_medium_3)}
                />
              </>
            )}

            {/* ======= IDENTIFICATION MARKS ======= */}
            <SectionHeader
              title={
                t?.identificationMarksOnBody ||
                "Identification Marks on the Body"
              }
            />
            <PreviewRow
              label={t?.identificationMark1 || "Identification Mark 1"}
              value={formData.identification_mark_1}
            />
            <PreviewRow
              label={t?.identificationMark2 || "Identification Mark 2"}
              value={formData.identification_mark_2}
            />

            {/* ======= OTHER DETAILS ======= */}
            <SectionHeader title={t?.otherDetails || "Other Details"} />
            <PreviewRow label={t?.place || "Place"} value={formData.place} />
            <PreviewRow
              label={t?.studentImage || "Student Image"}
              value={formData.student_image_original_name}
            />
          </tbody>
        </table>

        {/* ======= AFFIDAVIT SECTION ======= */}
        <div
          style={{
            padding: "20px 24px",
            borderTop: "2px solid #e0e0e0",
            marginTop: "8px",
          }}
        >
          <h5
            style={{ fontWeight: 700, fontSize: "16px", marginBottom: "16px" }}
          >
            {t?.parentGuardianAffidavit || "Parent / Guardian Affidavit"}
          </h5>
          <p style={{ fontSize: "13px", lineHeight: 1.7, color: "#333" }}>
            {t?.affidavitLine1 ||
              `I hereby undertake that my Son, ${studentName}, shall strictly adhere to the rules and regulations of the school.`}
          </p>
          <p style={{ fontSize: "13px", lineHeight: 1.7, color: "#333" }}>
            {t?.affidavitLine2 ||
              "In case of any misconduct, I agree to request and obtain the Transfer Certificate."}
          </p>
          <p style={{ fontSize: "13px", lineHeight: 1.7, color: "#333" }}>
            {t?.affidavitLine3 ||
              "I hereby certify that the above details are correct to the best of my knowledge."}
            <br />
            {t?.affidavitLine4 ||
              'I further affirm that my son/daughter has not been enrolled in or studied at any school other than the one specified under "Previous School Address".'}
          </p>
          <p style={{ fontSize: "13px", lineHeight: 1.7, color: "#333" }}>
            {t?.affidavitLine5 ||
              "I hereby declare that the date of birth and community details furnished in the admission application are true and correct."}
            <br />
            {t?.affidavitLine6 ||
              "I further undertake that I will not apply for or request any change in these particulars at any time in the future."}
          </p>

          {/* ---- Checkbox ---- */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginTop: "16px",
              marginBottom: "20px",
            }}
          >
            <input
              type="checkbox"
              id="agreeCheckbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{
                width: "18px",
                height: "18px",
                accentColor: "#1565c0",
                cursor: "pointer",
              }}
            />
            <label
              htmlFor="agreeCheckbox"
              style={{ fontSize: "14px", fontWeight: 500, cursor: "pointer" }}
            >
              {t?.iAgreeToAboveStatements ||
                "I have read and agree to the above statements."}
            </label>
          </div>

          {/* ---- OTP Section — shown only after checkbox ticked ---- */}
          {/* {agreed && ( */}
          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              padding: "16px 20px",
              backgroundColor: "#fafafa",
            }}
          >
            <p
              style={{
                fontSize: "14px",
                fontWeight: 500,
                marginBottom: "12px",
              }}
            >
              {t?.enterOtpSentToEmail ||
                "Enter the 6-digit OTP sent to your email"}
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              {/* OTP input — numbers only, max 6, live match check */}
              <div style={{ maxWidth: "200px" }}>
                <input
                  ref={otpInputRef}
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={handleOtpChange}
                  placeholder="------"
                  maxLength={6}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: otpError
                      ? "1px solid #d32f2f"
                      : otpVerified
                        ? "1px solid #2e7d32"
                        : "1px solid #ccc",
                    borderRadius: "6px",
                    fontSize: "20px",
                    letterSpacing: "6px",
                    textAlign: "center",
                    outline: "none",
                    fontWeight: 600,
                  }}
                />
                {/* Error message */}
                {otpError && (
                  <div
                    style={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {otpError}
                  </div>
                )}
                {/* Success indicator */}
                {otpVerified && (
                  <div
                    style={{
                      color: "#2e7d32",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {t?.otpVerified || "✓ OTP verified"}
                  </div>
                )}
              </div>

              {/* Resend OTP button */}
              <CustomButton
                type="button"
                variant="outlined"
                onClick={handleResend}
                loading={otpSending}
                disabled={otpTimer > 0}
                fullWidth={false}
              >
                {t?.resendOtp || "Resend OTP"}
              </CustomButton>
            </div>

            {otpTimer > 0 && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  marginTop: "8px",
                  marginBottom: 0,
                }}
              >
                {t?.otpExpiresIn || "OTP expires in"} {formatTimer(otpTimer)}
              </p>
            )}
          </div>
          {/* // )} */}
        </div>
      </Modal.Body>

      {/* ---- Modal Footer ---- */}
      <Modal.Footer
        style={{
          borderTop: "1px solid #e0e0e0",
          padding: "12px 20px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
        }}
      >
        <CustomButton
          type="button"
          variant="contained"
          onClick={handleClose}
          fullWidth={false}
          style={{
            backgroundColor: "#555",
            color: "#fff",
            padding: "8px 24px",
          }}
        >
          {t?.edit || "Edit"}
        </CustomButton>

        {/* Confirm & Submit — enabled only when checkbox ticked AND OTP verified */}
        <CustomButton
          type="button"
          variant="contained"
          onClick={handleConfirmSubmit}
          fullWidth={false}
          loading={submitting}
          disabled={!canSubmit || submitting}
          style={{
            backgroundColor: canSubmit ? "#2e7d32" : "#9e9e9e",
            color: "#fff",
            padding: "8px 24px",
            cursor: canSubmit ? "pointer" : "not-allowed",
          }}
        >
          {t?.confirmAndSubmit || "Confirm & Submit"}
        </CustomButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalX;
